import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { SelectButton } from "primereact/selectbutton";

import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";

import Loading from "../../../components/Loading";
import Menu from "../../../components/Menu";
import { GraficoEntradasSaidas } from "../../../components/Graficos/GraficoEntradasSaidas";

import api from "../../../services/api";

const PERIODOS = [
    { label: "Dia", value: "dia" },
    { label: "Semana", value: "semana" },
    { label: "Mês", value: "mes" },
];

const NOMES_PERIODO = {
    dia: "Dia",
    semana: "Semana",
    mes: "Mês",
};

const doisDigitos = (n) => String(n).padStart(2, "0");

const formatarISO = (date) => {
    const y = date.getFullYear();
    const m = doisDigitos(date.getMonth() + 1);
    const d = doisDigitos(date.getDate());
    return `${y}-${m}-${d}`;
};

const parseISO = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
};

const formatarDiaMes = (iso) => {
    const [, m, d] = iso.split("-");
    return `${d}/${m}`;
};

const formatarDataCompleta = (date) =>
    `${doisDigitos(date.getDate())}/${doisDigitos(date.getMonth() + 1)}/${date.getFullYear()}`;

const formatarNumero = (n) =>
    Number(n).toLocaleString("pt-BR", { maximumFractionDigits: 0 });

const primeiroDiaDoMes = () => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1);
};

// Segunda-feira da semana da data informada
const inicioDaSemana = (date) => {
    const dow = (date.getDay() + 6) % 7; // segunda = 0
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - dow);
};

const chaveDoPeriodo = (iso, periodo) => {
    if (periodo === "dia") return iso;

    const date = parseISO(iso);
    if (periodo === "semana") return formatarISO(inicioDaSemana(date));
    return `${date.getFullYear()}-${doisDigitos(date.getMonth() + 1)}`;
};

const labelDoPeriodo = (chave, info, periodo) => {
    if (periodo === "dia") return formatarDiaMes(chave);

    if (periodo === "semana") {
        return info.primeiro === info.ultimo
            ? formatarDiaMes(info.primeiro)
            : `${formatarDiaMes(info.primeiro)} - ${formatarDiaMes(info.ultimo)}`;
    }

    const [ano, mes] = chave.split("-");
    return `${mes}/${ano}`;
};

/**
 * Agrupa os dados por período (dia/semana/mês).
 * - porArtigo = false: soma todos os artigos (artigo = null)
 * - porArtigo = true: mantém uma linha por artigo dentro de cada período
 * Retorna: { periodos: [{ chave, label }], linhas: [{ chave, label, artigo, entrada, saida }] }
 */
const agruparDados = (dados, periodo, porArtigo) => {
    const infoPeriodos = new Map();
    const linhasMap = new Map();

    dados.forEach(i => {
        const chave = chaveDoPeriodo(i.data_movimento, periodo);
        const artigo = porArtigo ? String(i.artigo_codigo) : null;

        if (!infoPeriodos.has(chave)) {
            infoPeriodos.set(chave, { primeiro: i.data_movimento, ultimo: i.data_movimento });
        }
        const info = infoPeriodos.get(chave);
        if (i.data_movimento < info.primeiro) info.primeiro = i.data_movimento;
        if (i.data_movimento > info.ultimo) info.ultimo = i.data_movimento;

        const k = `${chave}|${artigo ?? ""}`;
        if (!linhasMap.has(k)) {
            linhasMap.set(k, { chave, artigo, entrada: 0, saida: 0 });
        }
        const l = linhasMap.get(k);
        l.entrada += Number(i.total_entrada);
        l.saida += Number(i.total_saida);
    });

    const periodos = Array.from(infoPeriodos.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([chave, info]) => ({ chave, label: labelDoPeriodo(chave, info, periodo) }));

    const labelPorChave = new Map(periodos.map(p => [p.chave, p.label]));

    const linhas = Array.from(linhasMap.values())
        .sort((a, b) =>
            a.chave.localeCompare(b.chave) ||
            String(a.artigo ?? "").localeCompare(String(b.artigo ?? ""), "pt-BR", { numeric: true })
        )
        .map(l => ({ ...l, label: labelPorChave.get(l.chave) }));

    return { periodos, linhas };
};

// Variações de cor para quando há mais de um artigo (verde = entrada, vermelho = saída)
const corEntrada = (idx) => `hsl(168, 65%, ${Math.min(35 + idx * 12, 75)}%)`;
const corSaida = (idx) => `hsl(6, 75%, ${Math.min(50 + idx * 8, 80)}%)`;

export const EntradasSaidas = () => {
    const ref = useRef(null);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [dataInicio, setDataInicio] = useState(primeiroDiaDoMes());
    const [dataFinal, setDataFinal] = useState(new Date());
    const [periodo, setPeriodo] = useState("dia");

    // Artigos escolhidos no filtro (ainda não aplicados)
    const [artigosSel, setArtigosSel] = useState([]);
    // Artigos usados na última consulta (define como o gráfico é exibido)
    const [artigosAplicados, setArtigosAplicados] = useState([]);
    // Opções do filtro, montadas a partir dos dados que a API devolve
    const [opcoesArtigos, setOpcoesArtigos] = useState([]);

    const fetchData = useCallback(() => {
        if (!dataInicio || !dataFinal) return;

        const filtro = artigosSel;

        setLoading(true);
        api.post("entradas-saidas-estoque", {
            data_inicio: formatarISO(dataInicio),
            data_final: formatarISO(dataFinal),
            artigos: filtro,
        }).then(response => {
            const lista = response.data?.data || [];

            setData(lista);
            setArtigosAplicados(filtro);

            // Acumula as opções de artigo (sem perder as já conhecidas)
            setOpcoesArtigos(prev => {
                const mapa = new Map(prev.map(o => [o.value, o]));
                lista.forEach(i => {
                    if (i.cod_artigo == null) return;
                    if (!mapa.has(i.cod_artigo)) {
                        mapa.set(i.cod_artigo, {
                            value: i.cod_artigo,
                            label: String(i.artigo_codigo),
                        });
                    }
                });
                return Array.from(mapa.values()).sort((a, b) =>
                    a.label.localeCompare(b.label, "pt-BR", { numeric: true })
                );
            });

            setLoading(false);
        }).catch(error => {
            setLoading(false);
            setData([]);
        });
    }, [dataInicio, dataFinal, artigosSel]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const porArtigo = artigosAplicados.length > 0;

    const totais = useMemo(() => {
        const entrada = data.reduce((acc, i) => acc + Number(i.total_entrada), 0);
        const saida = data.reduce((acc, i) => acc + Number(i.total_saida), 0);
        return { entrada, saida, saldo: entrada - saida };
    }, [data]);

    const { periodos, linhas } = useMemo(
        () => agruparDados(data, periodo, porArtigo),
        [data, periodo, porArtigo]
    );

    const textoArtigos = useMemo(() => {
        if (!porArtigo) return "Todos (total geral)";
        return artigosAplicados
            .map(cod => opcoesArtigos.find(o => o.value === cod)?.label ?? cod)
            .join(", ");
    }, [porArtigo, artigosAplicados, opcoesArtigos]);

    const chartData = useMemo(() => {
        const labels = periodos.map(p => p.label);

        // Sem filtro de artigo: apenas total de entrada e saída
        if (!porArtigo) {
            const porChave = new Map(linhas.map(l => [l.chave, l]));
            return {
                labels,
                datasets: [
                    {
                        label: "Entrada",
                        data: periodos.map(p => porChave.get(p.chave)?.entrada ?? 0),
                        backgroundColor: "#1abc9c",
                    },
                    {
                        label: "Saída",
                        data: periodos.map(p => porChave.get(p.chave)?.saida ?? 0),
                        backgroundColor: "#e74c3c",
                    },
                ],
            };
        }

        // Com filtro: um par de barras (entrada/saída) por artigo
        const artigos = Array.from(new Set(linhas.map(l => l.artigo)))
            .sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }));

        const datasets = [];
        artigos.forEach((art, idx) => {
            const doArtigo = new Map(linhas.filter(l => l.artigo === art).map(l => [l.chave, l]));
            const unico = artigos.length === 1;

            datasets.push({
                label: unico ? "Entrada" : `${art} - Entrada`,
                data: periodos.map(p => doArtigo.get(p.chave)?.entrada ?? 0),
                backgroundColor: unico ? "#1abc9c" : corEntrada(idx),
            });
            datasets.push({
                label: unico ? "Saída" : `${art} - Saída`,
                data: periodos.map(p => doArtigo.get(p.chave)?.saida ?? 0),
                backgroundColor: unico ? "#e74c3c" : corSaida(idx),
            });
        });

        return { labels, datasets };
    }, [periodos, linhas, porArtigo]);

    const nomeArquivo = (extensao) =>
        `Entradas_Saidas_${NOMES_PERIODO[periodo]}_${formatarISO(dataInicio)}_${formatarISO(dataFinal)}.${extensao}`;

    const handlePdf = async () => {
        const element = ref.current;
        if (!element) return;

        const opt = {
            margin: 12,
            filename: nomeArquivo("pdf"),
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "landscape" }
        };

        // Ativa o modo de impressão e aguarda o gráfico redesenhar no novo tamanho
        setPrinting(true);
        await new Promise(resolve => setTimeout(resolve, 600));

        try {
            await html2pdf().set(opt).from(element).save();
        } finally {
            setPrinting(false);
        }
    };

    const handleExcel = () => {
        const linhasExportacao = linhas.map(l => {
            const base = {
                "Período": l.label,
                ...(porArtigo ? { "Artigo": l.artigo } : {}),
                "Entradas": l.entrada,
                "Saídas": l.saida,
                "Saldo": l.entrada - l.saida,
            };
            return base;
        });

        linhasExportacao.push({
            "Período": "TOTAL",
            ...(porArtigo ? { "Artigo": "" } : {}),
            "Entradas": totais.entrada,
            "Saídas": totais.saida,
            "Saldo": totais.saldo,
        });

        const worksheet = XLSX.utils.json_to_sheet(linhasExportacao);
        worksheet["!cols"] = porArtigo
            ? [{ wch: 22 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 16 }]
            : [{ wch: 22 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Entradas x Saidas");

        XLSX.writeFile(workbook, nomeArquivo("xlsx"));
    };

    if (loading)
        return <Loading />

    return (
        <div>
            <Menu />

            <div style={{ padding: "1rem" }}>
                <h3>Entradas x Saídas</h3>

                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
                    <Calendar
                        value={dataInicio}
                        onChange={(e) => setDataInicio(e.value)}
                        dateFormat="dd/mm/yy"
                        maxDate={dataFinal || undefined}
                        showIcon
                    />
                    <Calendar
                        value={dataFinal}
                        onChange={(e) => setDataFinal(e.value)}
                        dateFormat="dd/mm/yy"
                        minDate={dataInicio || undefined}
                        showIcon
                    />

                    <MultiSelect
                        value={artigosSel}
                        options={opcoesArtigos}
                        onChange={(e) => setArtigosSel((e.value || []).slice(0, 5))}
                        selectionLimit={5}
                        placeholder="Todos os artigos (máx. 5)"
                        filter
                        showClear
                        maxSelectedLabels={3}
                        selectedItemsLabel="{0} artigos"
                        style={{ minWidth: "14rem" }}
                    />

                    <Button icon="pi pi-search" onClick={fetchData} tooltip="Filtrar" />

                    <SelectButton
                        value={periodo}
                        options={PERIODOS}
                        onChange={(e) => e.value && setPeriodo(e.value)}
                    />

                    <Button
                        className="p-button-danger"
                        icon="pi pi-file-pdf"
                        onClick={handlePdf}
                        disabled={printing || data.length === 0}
                        title="Download de PDF"
                    />
                    <Button
                        className="p-button-success"
                        icon="pi pi-file-excel"
                        onClick={handleExcel}
                        disabled={printing || data.length === 0}
                        title="Exportar para Excel"
                    />
                </div>

                {/* Tudo que estiver dentro deste bloco vai para o PDF */}
                <div ref={ref} style={{ background: "#fff", padding: printing ? "0" : undefined }}>
                    <div style={{ marginBottom: "1rem" }}>
                        <div style={{ fontWeight: "bold", fontSize: "1rem" }}>
                            Entradas x Saídas de Estoque
                        </div>
                        <div style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                            Período: {formatarDataCompleta(dataInicio)} a {formatarDataCompleta(dataFinal)} | Agrupamento: {NOMES_PERIODO[periodo]} | Artigos: {textoArtigos}
                        </div>
                        <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>
                            <div>Entradas: {formatarNumero(totais.entrada)}</div>
                            <div>Saídas: {formatarNumero(totais.saida)}</div>
                            <div>Saldo: {formatarNumero(totais.saldo)}</div>
                        </div>
                    </div>

                    <GraficoEntradasSaidas
                        chartId="grafico-entradas-saidas"
                        chartType="bar"
                        chartData={chartData}
                        printing={printing}
                    />
                </div>
            </div>
        </div>
    );
};